/**
 * 팀 모니터링 — 기능 동결·스킨 교체 (2026-10-14 워크스페이스 디자인: 레퍼런스 «compliance matrix» 카드)
 *
 * 기능(불변): scope 토글(me/team/global) · KPI 3개 클릭→드릴다운 모달(벌크 액션 포함)
 * 모양: 담당자마다 한 줄 — 이름 · 건수 / 한 칸 = 미처리 한 건(메뉴별 색). 아래에 메뉴 색 범례.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { RotateCw, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/shadcn/button';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/shadcn/tabs';
import EmptyState from '../../../components/ui/EmptyState';
import Tooltip from '../../../components/ui/Tooltip';
import { WsCells, wsTone, type WsTone } from '../../../components/ws';
import monitoringService, { MonitoringOverview, MonitoringScope, MonitoringKpi, PerAssignee } from '../../../services/monitoring';
import DrillDownModal from '../../../components/dashboard/widgets/DrillDownModal';

/** 메뉴별 칸 색 — 범례와 같은 순서 */
const MENUS: Array<{ key: keyof PerAssignee['counts']; label: string; tone: WsTone }> = [
  { key: 'claims', label: '클레임', tone: 'red' },
  { key: 'production', label: '생산', tone: 'amber' },
  { key: 'sales', label: '판매', tone: 'ink' },
  { key: 'purchase', label: '구매', tone: 'purple' },
  { key: 'home_styling', label: '홈스타일링', tone: 'green' },
];

/** 한 줄에 그릴 칸 최대 수 — 넘으면 «+n» */
const MAX_CELLS = 64;

const TeamMonitoringView: React.FC = () => {
  const [scope, setScope] = useState<MonitoringScope>('me');
  const [data, setData] = useState<MonitoringOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openKpi, setOpenKpi] = useState<MonitoringKpi | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await monitoringService.getOverview({ scope });
      setData(res);
    } catch (e: any) {
      if (e?.response?.status === 403) {
        setError('해당 scope 조회 권한이 없습니다.');
      } else {
        setError(e?.message || '모니터링 데이터 조회 실패');
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals;
  const people = [...(data?.per_assignee || [])]
    .map((p) => ({ ...p, total: MENUS.reduce((s, m) => s + (p.counts[m.key] || 0), 0) }))
    .filter((p) => p.total > 0)
    .sort((a, b) => b.total - a.total);

  const kpis: Array<{ key: MonitoringKpi; label: string; value: number; tone?: WsTone }> = [
    { key: 'pending', label: '미처리 총합', value: t?.pending ?? 0 },
    { key: 'overdue', label: '7일 초과', value: t?.overdue ?? 0, tone: (t?.overdue ?? 0) > 0 ? 'red' : undefined },
    { key: 'completed_today', label: '오늘 완료', value: t?.completed_today ?? 0, tone: (t?.completed_today ?? 0) > 0 ? 'green' : undefined },
  ];

  return (
    <div data-testid="team-monitoring" className="ws-bezel">
      <div className="ws-card" style={{ padding: '20px 22px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <h2 className="ws-h2">팀 업무 지도</h2>
            <div className="ws-sub" style={{ marginTop: 3 }}>담당자별 미처리 · 한 칸이 한 건</div>
          </div>
          <Tooltip label="새로고침">
            <Button type="button" variant="ghost" className="ws-btn ws-btn--icon ws-btn--sm" aria-label="팀 모니터링 새로고침" onClick={load} disabled={loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />}
            </Button>
          </Tooltip>
        </div>

        <Tabs value={scope} onValueChange={(v) => setScope(v as MonitoringScope)}>
          <TabsList className="ws-seg ws-seg--sm" style={{ marginTop: 14, height: 'auto', display: 'flex', width: '100%' }}>
            <TabsTrigger value="me" className="ws-seg-item" style={{ flex: 1 }}>내 업무</TabsTrigger>
            <TabsTrigger value="team" className="ws-seg-item" style={{ flex: 1 }}>우리 팀</TabsTrigger>
            <TabsTrigger value="global" className="ws-seg-item" style={{ flex: 1 }}>전사</TabsTrigger>
          </TabsList>
        </Tabs>

        {error ? (
          <div style={{ marginTop: 16 }}>
            <EmptyState variant="error" title="모니터링을 불러오지 못했습니다" description={error} onRetry={load} />
          </div>
        ) : (
          <>
            {/* KPI 3개 — 누르면 드릴다운 모달 (기능 동결) */}
            <div className="tm-kpis">
              {kpis.map((k) => (
                <Button
                  key={k.key}
                  type="button"
                  variant="ghost"
                  className="ws-btn ws-btn--ghost tm-kpi"
                  onClick={() => setOpenKpi(k.key)}
                  title="눌러서 상세 보기"
                >
                  <span className="ws-eyebrow" style={{ fontSize: 10.5 }}>{k.label}</span>
                  <span className={`ws-num ${k.tone ? wsTone(k.tone) : ''}`} style={{ fontSize: 24, fontWeight: 450, letterSpacing: '-0.02em', color: k.tone ? 'var(--tone-ink)' : 'var(--ws-ink)' }}>
                    {k.value.toLocaleString()}
                  </span>
                </Button>
              ))}
            </div>

            {/* 담당자별 칸 지도 */}
            <div style={{ marginTop: 6 }}>
              {loading && !data ? (
                [0, 1, 2, 3].map((i) => (
                  <div key={i} style={{ padding: '10px 0' }}>
                    <div className="ws-skel" style={{ height: 12, width: '40%' }} />
                    <div className="ws-skel" style={{ height: 12, width: '85%', marginTop: 8 }} />
                  </div>
                ))
              ) : people.length === 0 ? (
                <div className="ws-sub" style={{ padding: '16px 0 4px' }}>미처리 업무가 없습니다</div>
              ) : (
                people.slice(0, 8).map((p) => {
                  const segs = MENUS.map((m) => ({ tone: m.tone, count: p.counts[m.key] || 0 }));
                  return (
                    <div key={p.user_id} className="tm-row">
                      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                        <span className="ws-truncate" style={{ fontSize: 14, color: 'var(--ws-ink-2)' }}>{p.name}</span>
                        <span className="ws-mono ws-muted" style={{ fontSize: 12.5, flexShrink: 0 }}>
                          {p.total.toLocaleString()}건
                        </span>
                      </div>
                      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <WsCells segments={segs} max={MAX_CELLS} label={`${p.name} 미처리 ${p.total}건`} />
                        {p.total > MAX_CELLS && <span className="ws-mono ws-faint" style={{ fontSize: 11 }}>+{p.total - MAX_CELLS}</span>}
                      </div>
                    </div>
                  );
                })
              )}
              {people.length > 8 && <div className="ws-sub" style={{ paddingTop: 8 }}>외 {people.length - 8}명</div>}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--ws-line-soft)' }}>
              {MENUS.map((m) => (
                <span key={m.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--ws-ink-3)' }}>
                  <i className={`ws-cell ${wsTone(m.tone)}`} style={{ width: 9, height: 9 }} aria-hidden />
                  {m.label}
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      {/* KPI 드릴다운 모달 (무수정 재사용 — 벌크 상태변경/삭제/AI 상담) */}
      <DrillDownModal
        open={openKpi !== null}
        onClose={() => setOpenKpi(null)}
        kpi={openKpi}
        scope={scope}
      />
    </div>
  );
};

export default TeamMonitoringView;
